import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../../lib/http/ApiError';
import { useFetch } from '../../../hooks/useFetch';
import { useNotification } from '../../notifications/hooks/useNotification';
import { RightsProfilesApiFactory } from '../api/rights-profiles.api';
import type { PaginationMeta } from '../../../components/ui/tableTemplate/TableTemplateTypes';
import type { RightsProfile, RightsProfilesQueryParams } from '../types/rights-profile.types';

export interface UseGetRightsProfilesOptions {
  /**
   * Raise a global toast on a 5xx / network failure (default `true`, same rule as
   * `useGetUsers`). `ProfileSelector` passes `false`: it shows its own inline error.
   */
  notifyOnError?: boolean;
}

export interface UseGetRightsProfilesResult {
  /** null until the first response, kept while a reload is in flight, null again on error. */
  data: RightsProfile[] | null;
  /** Pagination of `data`: same lifecycle (null before the first response and on error). */
  meta: PaginationMeta | null;
  loading: boolean;
  error: ApiError | Error | null;
  refetch: () => void;
}

/**
 * Query hook for the paginated profile list (HOMCO-1413: `{ data, meta }`). Params
 * are serialized into a stable key, so a re-fetch fires when they change without
 * requiring a referentially stable object from the caller.
 *
 * Every load — including `refetch` — goes through the effect and its
 * AbortController (a `revision` counter in the effect dependencies), so a late
 * response is never applied over a newer one.
 */
export function useGetRightsProfiles(
  params?: RightsProfilesQueryParams,
  { notifyOnError = true }: UseGetRightsProfilesOptions = {},
): UseGetRightsProfilesResult {
  const [client, loading] = useFetch();
  const api = useMemo(() => RightsProfilesApiFactory(client), [client]);
  const { notifyError } = useNotification();

  const [data, setData] = useState<RightsProfile[] | null>(null);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [revision, setRevision] = useState(0);

  const paramsKey = JSON.stringify(params ?? {});
  const refetch = useCallback(() => setRevision(r => r + 1), []);

  useEffect(() => {
    const ctrl = new AbortController();
    const requested = JSON.parse(paramsKey) as RightsProfilesQueryParams;
    void (async () => {
      try {
        // Cleared before the request (not after it) so a reload never shows the
        // previous error; every other setState runs once the response is read.
        setError(null);
        const res = await api.getAll(requested, { signal: ctrl.signal });
        // Aborted while its response was already read: a newer request replaced it.
        if (ctrl.signal.aborted) return;
        setData(res.data);
        setMeta(res.meta);
      } catch (err) {
        // A replaced request that fails must not override the newer one either.
        if (ctrl.signal.aborted) return;
        if (err instanceof Error && err.name === 'AbortError') return;
        const e = err as ApiError | Error;
        setError(e);
        // Drop the previous page: its rows would read as the answer to the failed
        // request (e.g. page 1 shown under a "page 2" pagination bar).
        setData(null);
        setMeta(null);
        if (!notifyOnError) return;
        // 5xx / network: global toast — the user cannot act on it.
        if (ApiError.isApiError(e) && e.isServerError) notifyError(e);
        else if (!ApiError.isApiError(e)) notifyError(e);
      }
    })();
    return () => ctrl.abort();
  }, [api, notifyError, notifyOnError, paramsKey, revision]);

  return { data, meta, loading, error, refetch };
}
